import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { InputmaskOptions } from '@ngneat/input-mask';
import { SelectionChangedEvent } from 'ag-grid-enterprise';
import { Subscription, from } from 'rxjs';
import { ContactForm } from 'src/app/classes/form-groups/contact-form';
import { Contact, Contract, Site } from 'src/app/classes/models';
import { SubNationalDivision } from 'src/app/classes/models/sub-national-division';
import { LocalizationInfo } from 'src/app/interfaces/localization-info';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyService } from 'src/app/services/company.service';
import { ContactService } from 'src/app/services/contact.service';
import { ContractService } from 'src/app/services/contract.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { SubNationalDivisionService } from 'src/app/services/sub-national-division.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { companiesService } from 'src/app/shared/vive-api/companies.service';

@Component({
  selector: 'app-contract-site-selection',
  templateUrl: './contract-site-selection.component.html',
  styleUrls: ['./contract-site-selection.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ContractSiteSelectionComponent implements OnDestroy {
  siteContactForm: ContactForm = new ContactForm();

  selectedSite: Site | null = null;
  localizationInfo!: LocalizationInfo;
  states!: SubNationalDivision[];
  ContractId!: number;
  sharedDataServiceSubscription!: Subscription;
  selectedRowsPrimaryKeys: Set<string | number> = new Set();
  hasSiteContact: boolean = false;

  phoneMask = this.UtilityService.phoneMask;
  zipMask?: InputmaskOptions<any>;

  constructor(
    private ToastService: ToastService,
    private CompanyService: CompanyService,
    private ContractService: ContractService,
    private ContactService: ContactService,
    private AuthService: AuthService,
    private UtilityService: UtilityService,
    private SubNationalDivisionService: SubNationalDivisionService,
    private SharedDataService: SharedDataService,
    private route: ActivatedRoute
  ) {
    // isStreet is always set to true for now
    const StreetAddressFormControl =
      this.siteContactForm.controls.StreetAddress;
    if (StreetAddressFormControl) {
      StreetAddressFormControl.controls.isStreet.setValue(true);
      StreetAddressFormControl.updateValueAndValidity();
    }

    this.siteContactForm.removeControl('MailingAddress');

    const paramMap = this.route.snapshot.paramMap;
    const ContractId = Number(paramMap.get('ContractId'));

    this.ContractId = ContractId;

    this.ContractService.getContract({
      id: ContractId,
      Site: JSON.stringify({}),
      SiteContact: JSON.stringify({
        StreetAddress: {}
      })
    }).subscribe({
      next: (Contract: Contract) => {
        if (!Contract) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No Contract Record Found.',
            { returnError: true }
          );
        }

        const { SiteContact, Sites, id } = Contract;

        if (SiteContact) {
          this.hasSiteContact = true;
          this.siteContactForm.patchValue(SiteContact);
        }

        //  Right now, only one site per contract but this may change in future.
        if (Sites && Sites.length > 0) {
          const site = Sites[0];

          this.selectedSite = site;
          const { id } = this.selectedSite;

          if (id) {
            this.selectedRowsPrimaryKeys.add(id);
          }
        }
      }
    });

    from(
      companiesService.findOneById({
        pathParams: { companyId: this.AuthService.getUserCompanyId() }
      })
    ).subscribe({
      next: (Company) => {
        if (!Company) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No Company Record Found.',
            { returnError: true }
          );
        }

        const { country } = Company;

        if (country) {
          this.localizationInfo =
            this.UtilityService.getLocalizedValuesMap(country);

          this.SubNationalDivisionService.getSubNationalDivisions({
            country: country === 'US' ? 'USA' : 'Canada'
          }).subscribe({
            next: (data: SubNationalDivision[]) => {
              if (!data) {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'No SuNationalDivisions found.',
                  { returnError: true }
                );
              }

              this.states = data;
            }
          });
        }
      }
    });

    this.sharedDataServiceSubscription =
      this.SharedDataService.listenForEvent().subscribe({
        next: ({ eventName, data }) => {
          if (eventName === 'isFormValid') {
            const errors = this.UtilityService.generateValidationErrorsObject(
              this.siteContactForm
            );
            if (errors) {
              throw this.ToastService.addErrorToast(
                'Error',
                'All required fields must be completed to proceed.',
                { returnError: true }
              );
            } else {
              return this.SharedDataService.sendEvent('formIsValid');
            }
          }
        }
      });

    this.siteContactForm.valueChanges.subscribe({
      next: (changes) => {
        if (changes) {
          const SiteContactFormValue = new Contact({});
          Object.assign(SiteContactFormValue, {
            ...this.siteContactForm.getRawValue(),
            ContractId,
            isContractSite: true
          });
          this.SharedDataService.sendEvent(
            'UpdateSiteContact',
            SiteContactFormValue
          );
        }
      }
    });
  }

  onSiteSelectionChange(event: SelectionChangedEvent): void {
    const { api } = event;
    const selectedRow = api.getSelectedRows()[0];

    if (api.getSelectedRows().length > 1) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot select more than one site.',
        { returnError: true }
      );
    }

    this.selectedSite = selectedRow;

    if (this.selectedSite) {
      const { id } = this.selectedSite;

      this.SharedDataService.sendEvent(
        'UpdateContract',
        new Contract({ id: this.ContractId, Sites: [{ id }] })
      );

      if (!this.hasSiteContact) {
        this.ContactService.getContacts({
          SiteId: id,
          StreetAddress: JSON.stringify({})
        }).subscribe({
          next: (Contacts: Contact[]) => {
            const primaryContact = Contacts.find(
              (contact) => contact.isPrimary
            );
            const firstContact = Contacts.length > 0 ? Contacts[0] : null;

            if (primaryContact) {
              this.siteContactForm.patchValue(primaryContact);
            }

            if (!primaryContact && firstContact) {
              this.siteContactForm.patchValue(firstContact);
            }
          }
        });
      }
    }
  }

  ngOnDestroy(): void {
    this.sharedDataServiceSubscription.unsubscribe();
  }
}
